// Enhanced utility functions for drag state management with channel support

import { TimeSync } from './timeSync';
import { supabase } from '@/integrations/supabase/client';

export interface DragOperationTracker {
  operationId: string;
  timestamp: number;
  action: 'start' | 'update' | 'end' | 'clear';
  cardId: string;
  userId: string;
}

// New interface for channel message validation
export interface ChannelMessage {
  type: string;
  user_id: string;
  card_id: string;
  timestamp: string;
  [key: string]: any;
}

export class DragStateManager {
  private processedOperations = new Set<string>();
  private endedDrags = new Map<string, { timestamp: number; operationId: string }>();
  private sequenceNumber = 0;

  generateOperationId(userId: string, cardId: string, action: string): string {
    const serverTime = TimeSync.getServerTime();
    return `${userId}-${cardId}-${action}-${serverTime}-${++this.sequenceNumber}`;
  }

  markOperationProcessed(operationId: string): void {
    this.processedOperations.add(operationId);
  }

  isOperationProcessed(operationId: string): boolean {
    return this.processedOperations.has(operationId);
  }

  markDragEnded(userCardKey: string, operationId: string): void {
    this.endedDrags.set(userCardKey, {
      timestamp: TimeSync.getServerTime(),
      operationId
    });
  }

  isDragEnded(userCardKey: string, operationId?: string): boolean {
    const endedInfo = this.endedDrags.get(userCardKey);
    if (!endedInfo) return false;

    if (operationId && endedInfo.operationId) {
      const isOlderOperation = operationId < endedInfo.operationId;
      if (isOlderOperation) {
        return true;
      }
    }

    const timeSinceEnd = TimeSync.getServerTime() - endedInfo.timestamp;
    return timeSinceEnd < 1000;
  }

  cleanupOldOperations(maxAge: number = 15000): void {
    const now = TimeSync.getServerTime();
    
    const oldOperations = Array.from(this.processedOperations).filter(opId => {
      const timestampMatch = opId.match(/-(\d+)-\d+$/);
      if (!timestampMatch) return false;
      const timestamp = parseInt(timestampMatch[1]);
      return now - timestamp > maxAge;
    });
    oldOperations.forEach(opId => this.processedOperations.delete(opId));

    for (const [key, info] of this.endedDrags.entries()) {
      if (now - info.timestamp > maxAge) {
        this.endedDrags.delete(key);
      }
    }
  }

  reset(): void {
    this.processedOperations.clear();
    this.endedDrags.clear();
    this.sequenceNumber = 0;
  }

  getStats() {
    return {
      processedOperations: this.processedOperations.size,
      endedDrags: this.endedDrags.size,
      sequenceNumber: this.sequenceNumber
    };
  }
}

// Enhanced logging utilities with channel-specific logging
export const createDragLogger = (prefix: string = 'DragState') => ({
  success: (operation: string, data?: any) => {
    const timestamp = new Date().toISOString();
    console.log(`✅ [${prefix} ${timestamp}] ${operation} successful${data ? ':' : ''}`, data || '');
  },
  
  error: (operation: string, error: any, context?: any) => {
    const timestamp = new Date().toISOString();
    console.error(`❌ [${prefix} ${timestamp}] ${operation} failed:`, error);
    if (context) {
      console.error(`❌ [${prefix}] Context:`, context);
    }
  },
  
  info: (operation: string, data?: any) => {
    const timestamp = new Date().toISOString();
    console.log(`ℹ️ [${prefix} ${timestamp}] ${operation}`, data || '');
  },
  
  warning: (operation: string, data?: any) => {
    const timestamp = new Date().toISOString();
    console.warn(`⚠️ [${prefix} ${timestamp}] ${operation}`, data || '');
  },

  timing: (operation: string, startTime: number, data?: any) => {
    const duration = Date.now() - startTime;
    const timestamp = new Date().toISOString();
    const emoji = duration < 100 ? '⚡' : duration < 500 ? '⏱️' : '🐌';
    console.log(`${emoji} [${prefix} ${timestamp}] ${operation} took ${duration}ms`, data || '');
  }
});

export const validateRealtimeUpdate = (record: any, currentUserId?: string): {
  isValid: boolean;
  reason?: string;
} => {
  if (!record) {
    return { isValid: false, reason: 'No record provided' };
  }

  if (!record.card_id || !record.user_id || !record.board_id) {
    return { isValid: false, reason: 'Missing required fields' };
  }

  if (record.updated_at) {
    const age = TimeSync.getAge(record.updated_at);
    
    if (age > 30000) {
      return { isValid: false, reason: `Update too old: ${age} ms` };
    }
  }

  return { isValid: true };
};

// NEW: Channel message validation
export const validateChannelMessage = (message: any): {
  isValid: boolean;
  reason?: string;
} => {
  if (!message) {
    return { isValid: false, reason: 'No message provided' };
  }

  if (!message.type || !message.user_id || !message.card_id) {
    return { isValid: false, reason: 'Missing required message fields' };
  }

  if (message.timestamp) {
    const age = TimeSync.getAge(message.timestamp);
    
    if (age > 5000) { // Shorter threshold for channel messages
      return { isValid: false, reason: `Channel message too old: ${age} ms` };
    }
  }

  return { isValid: true };
};

// **PHASE 3: Enhanced position broadcast limiter with termination awareness**
export class PositionBroadcastLimiter {
  private lastBroadcasts = new Map<string, number>();
  private throttleInterval: number;
  private stabilityBuffer = new Map<string, number>();
  private terminatingKeys = new Set<string>(); // **PHASE 3: Track terminating drags**

  constructor(throttleInterval: number = 50) {
    this.throttleInterval = throttleInterval;
  }

  // **PHASE 3: Mark a drag as terminating**
  markTerminating(userCardKey: string): void {
    this.terminatingKeys.add(userCardKey);
    console.log(`🏁 [BROADCAST LIMITER] Marked ${userCardKey} as terminating`);
  }

  // **PHASE 3: Check if a drag is terminating**
  isTerminating(userCardKey: string): boolean {
    return this.terminatingKeys.has(userCardKey);
  }

  canBroadcast(userCardKey: string): boolean {
    const now = Date.now();
    const lastBroadcast = this.lastBroadcasts.get(userCardKey) || 0;
    const isTerminating = this.terminatingKeys.has(userCardKey);
    
    // **PHASE 3: Allow more frequent broadcasts for terminating drags**
    const effectiveInterval = isTerminating ? this.throttleInterval / 2 : this.throttleInterval;
    
    const stabilityCount = this.stabilityBuffer.get(userCardKey) || 0;
    
    if (now - lastBroadcast >= effectiveInterval) {
      this.lastBroadcasts.set(userCardKey, now);
      this.stabilityBuffer.set(userCardKey, Math.min(stabilityCount + 1, 5));
      return true;
    }
    
    // Reduce stability count for rapid requests (unless terminating)
    if (!isTerminating) {
      this.stabilityBuffer.set(userCardKey, Math.max(0, stabilityCount - 1));
    }
    return false;
  }

  cleanup(maxAge: number = 10000): void {
    const now = Date.now();
    
    // Clean up old broadcasts
    for (const [key, timestamp] of this.lastBroadcasts.entries()) {
      if (now - timestamp > maxAge) {
        this.lastBroadcasts.delete(key);
        this.stabilityBuffer.delete(key);
        this.terminatingKeys.delete(key); // **PHASE 3: Clean up terminating keys**
      }
    }
  }

  reset(): void {
    this.lastBroadcasts.clear();
    this.stabilityBuffer.clear();
    this.terminatingKeys.clear(); // **PHASE 3: Reset terminating keys**
  }
}

// **PHASE 3: Enhanced conflict resolution with exponential backoff**
export class ConflictResolver {
  private conflictCount = 0;
  private retryAttempts = new Map<string, number>();
  private logger = createDragLogger('ConflictResolver');

  async resolveConflict(data: any, maxRetries: number = 3): Promise<void> {
    const conflictKey = `${data.user_id}-${data.card_id}`;
    const currentAttempts = this.retryAttempts.get(conflictKey) || 0;
    
    if (currentAttempts >= maxRetries) {
      this.logger.error('Max retry attempts reached for conflict resolution', {
        conflictKey,
        attempts: currentAttempts
      });
      this.retryAttempts.delete(conflictKey);
      throw new Error(`Max retry attempts (${maxRetries}) reached for conflict resolution`);
    }

    this.conflictCount++;
    this.retryAttempts.set(conflictKey, currentAttempts + 1);
    
    // **PHASE 3: Exponential backoff with jitter**
    const baseDelay = Math.pow(2, currentAttempts) * 100; // 100ms, 200ms, 400ms
    const jitter = Math.random() * 50; // Add up to 50ms jitter
    const delay = baseDelay + jitter;
    
    this.logger.warning('🔧 Attempting conflict resolution with backoff', {
      attempt: currentAttempts + 1,
      maxRetries,
      delay: Math.round(delay),
      cardId: data.card_id,
      userId: data.user_id
    });

    // **PHASE 3: Wait before retry**
    await new Promise(resolve => setTimeout(resolve, delay));

    try {
      // Strategy: Force update with current timestamp
      const { error } = await supabase
        .from('drag_state')
        .update({
          target_column_id: data.target_column_id,
          drag_position: data.drag_position,
          is_active: data.is_active,
          updated_at: TimeSync.getServerISOString()
        })
        .match({
          user_id: data.user_id,
          card_id: data.card_id,
          board_id: data.board_id
        });

      if (error) {
        this.logger.error('Conflict resolution failed', error);
        throw error;
      }

      this.logger.success('Conflict resolved', { 
        cardId: data.card_id,
        attempts: currentAttempts + 1
      });
      
      // **PHASE 3: Clear retry count on success**
      this.retryAttempts.delete(conflictKey);
    } catch (error) {
      this.logger.error('Conflict resolution error', error);
      
      // **PHASE 3: Retry if we haven't hit max attempts**
      if (currentAttempts + 1 < maxRetries) {
        return this.resolveConflict(data, maxRetries);
      } else {
        this.retryAttempts.delete(conflictKey);
        throw error;
      }
    }
  }

  getStats() {
    return {
      conflictCount: this.conflictCount,
      activeRetries: this.retryAttempts.size
    };
  }
}

// **PHASE 2: Enhanced channel health monitor with termination awareness**
export class ChannelHealthMonitor {
  private isHealthy = true;
  private connectionErrors = 0;
  private lastErrorTime = 0;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 3;
  private baseReconnectDelay = 2000;
  private healthCallbacks: Array<(isHealthy: boolean) => void> = [];
  private consecutiveErrors = 0;
  private gracePeriodActive = false; // **PHASE 2: Grace period for drag terminations**

  // **PHASE 2: Activate grace period during drag terminations**
  activateGracePeriod(duration: number = 2000): void {
    this.gracePeriodActive = true;
    console.log(`🔗 [HEALTH] Grace period activated for ${duration}ms`);
    
    setTimeout(() => {
      this.gracePeriodActive = false;
      console.log('🔗 [HEALTH] Grace period ended');
    }, duration);
  }

  onConnectionError() {
    // **PHASE 2: Be more lenient during grace period**
    if (this.gracePeriodActive) {
      console.log('🔗 [HEALTH] Connection error during grace period - ignoring');
      return;
    }

    this.connectionErrors++;
    this.consecutiveErrors++;
    this.lastErrorTime = Date.now();
    
    // **PHASE 2: Require more consecutive errors and longer time**
    if (this.consecutiveErrors >= 5 && this.isHealthy) { // Increased from 3 to 5
      const timeSinceFirstError = Date.now() - this.lastErrorTime;
      
      // **PHASE 2: Require errors to persist for at least 3 seconds**
      if (timeSinceFirstError > 3000) {
        console.log('🔗 [HEALTH] Connection marked as unhealthy after sustained errors');
        this.isHealthy = false;
        this.notifyHealthChange();
      }
    }
  }

  onReconnectSuccess() {
    if (!this.isHealthy || this.connectionErrors > 0) {
      console.log('🔗 [HEALTH] Connection restored');
      this.isHealthy = true;
      this.connectionErrors = 0;
      this.consecutiveErrors = 0;
      this.reconnectAttempts = 0;
      this.gracePeriodActive = false; // **PHASE 2: Clear grace period**
      this.notifyHealthChange();
    }
  }

  onReconnectAttempt() {
    this.reconnectAttempts++;
    console.log(`🔗 [HEALTH] Reconnect attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts}`);
  }

  shouldReconnect(): boolean {
    const shouldReconnect = this.reconnectAttempts < this.maxReconnectAttempts;
    if (!shouldReconnect) {
      console.log('🔗 [HEALTH] Max reconnect attempts reached, entering graceful degradation mode');
    }
    return shouldReconnect;
  }

  getReconnectDelay(): number {
    // **PHASE 2: More reasonable exponential backoff**
    const delay = Math.min(
      this.baseReconnectDelay * Math.pow(1.5, this.reconnectAttempts),
      10000 // Cap at 10 seconds
    );
    console.log(`🔗 [HEALTH] Next reconnect in ${delay}ms`);
    return delay;
  }

  isConnectionHealthy(): boolean {
    return this.isHealthy;
  }

  getStats() {
    return {
      isHealthy: this.isHealthy,
      connectionErrors: this.connectionErrors,
      consecutiveErrors: this.consecutiveErrors,
      reconnectAttempts: this.reconnectAttempts,
      gracePeriodActive: this.gracePeriodActive, // **PHASE 2: Include grace period status**
      timeSinceLastError: this.lastErrorTime ? Date.now() - this.lastErrorTime : null
    };
  }

  addHealthCallback(callback: (isHealthy: boolean) => void) {
    this.healthCallbacks.push(callback);
  }

  removeHealthCallback(callback: (isHealthy: boolean) => void) {
    const index = this.healthCallbacks.indexOf(callback);
    if (index > -1) {
      this.healthCallbacks.splice(index, 1);
    }
  }

  private notifyHealthChange() {
    console.log(`🔗 [HEALTH] Health changed: ${this.isHealthy ? 'HEALTHY' : 'UNHEALTHY'}`);
    this.healthCallbacks.forEach(callback => {
      try {
        callback(this.isHealthy);
      } catch (error) {
        console.error('❌ Health callback error:', error);
      }
    });
  }

  reset() {
    console.log('🔗 [HEALTH] Resetting health monitor');
    this.isHealthy = true;
    this.connectionErrors = 0;
    this.consecutiveErrors = 0;
    this.lastErrorTime = 0;
    this.reconnectAttempts = 0;
    this.gracePeriodActive = false; // **PHASE 2: Reset grace period**
    this.notifyHealthChange();
  }
}
